import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { getTableActionsConfig } from '../../config/appConfig';
import { PencilIcon, TrashIcon, SpinnerIcon, ExclamationCircleIcon, ClipboardIcon, CheckCircleFilledIcon, CircleIcon } from '../ui/Icons';

/**
 * Generic Action Table Component
 * Displays data in a card-based layout using a simple configuration
 */
const GenericActionTable = ({
  data = [],
  config,
  isLoading = false,
  error = null,
  onEdit,
  onDelete,
  onRefresh,
  emptyStateMessage = "No data found",
  emptyStateSubMessage = "Click the add button to create new items",
  showActions = true,
  customActions = null,
  className = ""
}) => {
  const { theme } = useTheme();
  
  // Get table actions configuration
  const actionsVisibility = getTableActionsConfig('visibility');
  const showTooltips = getTableActionsConfig('showTooltips');
  const buttonSize = getTableActionsConfig('buttonSize');
  const spacing = getTableActionsConfig('spacing');

  // Helper function to get nested object value
  const getNestedValue = (obj, path) => {
    if (!path) return null;
    
    return path.split('.').reduce((current, key) => {
      return current && current[key] !== undefined ? current[key] : null;
    }, obj);
  };

  // Helper function to format cell value
  const formatCellValue = (item, fieldName, displayField) => {
    let value;
    
    if (displayField) {
      value = getNestedValue(item, displayField);
    } else {
      value = getNestedValue(item, fieldName);
    }
    
    // Return a placeholder for null/undefined values
    if (value === null || value === undefined) {
      return 'Not set';
    }

    // Handle different field types
    if (fieldName === 'completed') {
      return value ? 'Completed' : 'Pending';
    }
    
    if (fieldName === 'dueDate' && value) {
      return new Date(value).toLocaleDateString();
    }

    return value;
  };

  // Render a single data row
  const renderDataRow = (item, index) => {
    return (
      <div key={item.id || index} className={cn(
        "p-4 rounded-lg border transition-colors duration-200 group",
        theme.border.subtle, 
        theme.colors.background,
        "hover:border-opacity-60"
      )}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            {/* Header with title */}
            <div className="flex items-start justify-between gap-2 mb-3">
              {/* Main title/description field */}
              <div className={cn("text-sm font-medium", theme.text.primary)}>
                {item.description || item.name || item.title || `Item ${index + 1}`}
              </div>
              
              {/* Action buttons */}
              {showActions && (
                <div className={cn(
                  "flex items-center",
                  spacing,
                  actionsVisibility === 'hover' ? 'opacity-0 group-hover:opacity-100 transition-opacity duration-200' : 'opacity-100'
                )}>
                  {customActions ? (
                    customActions(item, index)
                  ) : (
                    <>
                      {onEdit && (
                        <button
                          onClick={() => onEdit(item)}
                          className={cn(
                            "rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200",
                            theme.text.secondary,
                            buttonSize === 'xs' ? 'p-0.5' : 
                            buttonSize === 'sm' ? 'p-1' : 
                            buttonSize === 'md' ? 'p-1.5' : 'p-2'
                          )}
                          title={showTooltips ? "Edit item" : undefined}
                        >
                          <PencilIcon className={cn(
                            buttonSize === 'xs' ? 'w-3 h-3' : 
                            buttonSize === 'sm' ? 'w-4 h-4' : 
                            buttonSize === 'md' ? 'w-5 h-5' : 'w-6 h-6'
                          )} />
                        </button>
                      )}
                      {onDelete && (
                        <button
                          onClick={() => onDelete(item.id || item)}
                          className={cn(
                            "rounded hover:bg-red-100 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 transition-colors duration-200",
                            buttonSize === 'xs' ? 'p-0.5' : 
                            buttonSize === 'sm' ? 'p-1' : 
                            buttonSize === 'md' ? 'p-1.5' : 'p-2'
                          )}
                          title={showTooltips ? "Delete item" : undefined}
                        >
                          <TrashIcon className={cn(
                            buttonSize === 'xs' ? 'w-3 h-3' : 
                            buttonSize === 'sm' ? 'w-4 h-4' : 
                            buttonSize === 'md' ? 'w-5 h-5' : 'w-6 h-6'
                          )} />
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
            
            {/* Data fields grid - Simple 2-column layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
              
              {/* Column 1 */}
              <div className="space-y-2">
                {/* Type */}
                <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                  <span className="font-medium">Type:</span>
                  <span>{formatCellValue(item, 'type', 'type.name')}</span>
                </div>
                
                {/* Assigned To */}
                <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                  <span className="font-medium">Assigned To:</span>
                  <span>{formatCellValue(item, 'user', 'user.userDisplayName')}</span>
                </div>
                
                {/* Due Date */}
                <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                  <span className="font-medium">Due Date:</span>
                  <span>{formatCellValue(item, 'dueDate')}</span>
                </div>
              </div>

              {/* Column 2 */}
              <div className="space-y-2">
                {/* Priority */}
                <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                  <span className="font-medium">Priority:</span>
                  <span>{formatCellValue(item, 'priority', 'priority.name')}</span>
                </div>
                
                {/* Status */}
                <div className={cn("flex items-center gap-2", theme.text.secondary)}>
                  <span className="font-medium">Status:</span>
                  <span>{formatCellValue(item, 'status', 'status.name')}</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Status badges (right side) */}
          <div className="flex-shrink-0 flex flex-col gap-2">
            {/* Status badge */}
            {item.status && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {item.status.name || item.status}
              </span>
            )}
            
            {/* Completed badge */}
            {item.completed !== undefined && (
              <span className={cn(
                "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium",
                item.completed 
                  ? "bg-green-100 text-green-800" 
                  : "bg-yellow-100 text-yellow-800"
              )}>
                {item.completed ? (
                  <>
                    <CheckCircleFilledIcon className="w-3 h-3 mr-1" />
                    Completed
                  </>
                ) : (
                  <>
                    <CircleIcon className="w-3 h-3 mr-1" />
                    Pending
                  </>
                )}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={cn("space-y-4", className)}>
      {/* Loading State */}
      {isLoading && (
        <div className={cn("text-center py-8", theme.text.muted)}>
          <div className="mb-4">
            <SpinnerIcon className="w-8 h-8 mx-auto" />
          </div>
          <div className="text-sm">Loading data...</div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className={cn("text-center py-8", theme.text.muted)}>
          <div className="mb-4">
            <ExclamationCircleIcon className="w-8 h-8 mx-auto text-red-400" />
          </div>
          <div className="text-sm text-red-600 mb-2">Error loading data</div>
          <div className="text-xs text-gray-500">{error.message || error}</div>
          {onRefresh && (
            <button
              onClick={onRefresh}
              className={cn("mt-2 px-3 py-1 text-xs rounded border", theme.border.light, theme.text.secondary)}
            >
              Retry
            </button>
          )}
        </div>
      )}

      {/* Data List */}
      {!isLoading && !error && data.length > 0 ? (
        <div className="space-y-3">
          {data.map((item, index) => renderDataRow(item, index))}
        </div>
      ) : (
        !isLoading && !error && (
          <div className={cn("text-center py-8", theme.text.muted)}>
          <div className="mb-4">
            <ClipboardIcon className="w-12 h-12 mx-auto text-gray-400" />
          </div>
            <div className="text-sm font-medium mb-2">{emptyStateMessage}</div>
            <div className="text-xs">{emptyStateSubMessage}</div>
          </div>
        )
      )}
    </div>
  );
};

export default GenericActionTable;
